import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, useTheme } from '@mui/material';

import { ProcessTalentResponseDTOAccessModesItem } from '@/api/generated';
import ProcessProgress from '@/components/ProcessProgress';
import { useProcessPermissions } from '@/hooks/permissions/useProcessPermissions';
import { isComplete, isRetest, isTalentFinished } from '@/utils/testSessions';

import FilePicker from './FilePicker';
import SendInvitationButton from './SendInvitationButton';
import { useTalentProcessContext } from './TalentProcessContext';

const ProcessSubHeader = () => {
  const { t } = useTranslation('processes');
  const { palette, spacing } = useTheme();
  const {
    process: { testSessionSummaries = [], accessModes, id: processId }
  } = useTalentProcessContext();
  const completedTestsCount = useMemo(
    () => testSessionSummaries.filter(testSession => isRetest(testSession) || isComplete(testSession)).length,
    [testSessionSummaries]
  );
  const progressValue = useMemo(() => {
    if (!testSessionSummaries.length) return 0;

    return Math.round((completedTestsCount / testSessionSummaries.length) * 100);
  }, [completedTestsCount, testSessionSummaries.length]);
  const { value: { canSendInvitation } = {} } = useProcessPermissions({
    variables: { processId }
  });
  const allowedToSendInvitation = useMemo(
    () =>
      canSendInvitation &&
      accessModes?.includes(ProcessTalentResponseDTOAccessModesItem.UNIQUE_LINK) &&
      !isTalentFinished(testSessionSummaries),
    [canSendInvitation, accessModes, testSessionSummaries]
  );

  return (
    <>
      <Typography variant="body2" marginInlineEnd={1}>
        {t('layouts.process.tests')}
      </Typography>
      <ProcessProgress value={progressValue} />
      <Typography variant="body3" alignSelf="center" color={palette.text.primary} marginInlineStart={1}>
        {t('layouts.process.testsProgress', { progress: progressValue })}
      </Typography>
      <SendInvitationButton disabled={!allowedToSendInvitation} sx={{ marginInlineStart: 'auto' }} />
      <FilePicker sx={{ marginInlineStart: spacing(1) }} />
    </>
  );
};

export default ProcessSubHeader;
